import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { type OutboundTranslationPreview } from '@libraries/typescript/api_client/schema_types';

type FetchPreviewArgs = {
    readonly organizationId: string;
    readonly content: string;
    readonly conversationId?: string;
    readonly caregiverId?: string;
};

// Ask the API to translate a coordinator-typed message into the recipient
// caregiver's language for an in-composer preview. Returns null fields when the
// caregiver speaks English / an unsupported language, so the caller sends the
// original text verbatim.
export async function fetchOutboundTranslationPreview({
    organizationId,
    content,
    conversationId,
    caregiverId,
}: FetchPreviewArgs): Promise<OutboundTranslationPreview> {
    return getApiClient().post({
        url: '/organizations/{organization_id}/conversations/translate-preview',
        pathParams: { organization_id: organizationId },
        body: {
            content,
            conversation_id: conversationId ?? null,
            caregiver_id: caregiverId ?? null,
        },
    });
}

type TranslationPreviewDialogProps = {
    readonly open: boolean;
    readonly englishSource: string;
    readonly languageName: string | null;
    readonly translatedText: string;
    readonly isSending: boolean;
    readonly onSendTranslated: (translatedBody: string) => void | Promise<void>;
    readonly onSendEnglish: () => void | Promise<void>;
    readonly onCancel: () => void;
};

// Confirmation step shown before sending a translated message: renders the
// coordinator's English alongside the editable translation, and offers an
// explicit choice between sending the translation or the original English.
export function TranslationPreviewDialog({
    open,
    englishSource,
    languageName,
    translatedText,
    isSending,
    onSendTranslated,
    onSendEnglish,
    onCancel,
}: TranslationPreviewDialogProps) {
    const [editedTranslation, setEditedTranslation] = useState(translatedText);

    // Re-seed the editable translation each time a new preview opens.
    useEffect(() => {
        if (open) {
            setEditedTranslation(translatedText);
        }
    }, [open, translatedText]);

    const languageLabel = languageName ?? 'the caregiver’s language';

    return (
        <Dialog
            onOpenChange={(nextOpen) => {
                if (!nextOpen && !isSending) {
                    onCancel();
                }
            }}
            open={open}
        >
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>Send in {languageLabel}?</DialogTitle>
                    <DialogDescription>
                        This caregiver prefers {languageLabel}. Review the
                        translation before sending, or send your original
                        English instead.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <p className="text-muted-foreground text-xs font-medium">
                            Your message (English)
                        </p>
                        <p className="bg-muted rounded-md p-3 text-sm whitespace-pre-wrap">
                            {englishSource}
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <p className="text-muted-foreground text-xs font-medium">
                            Translation ({languageLabel})
                        </p>
                        <Textarea
                            disabled={isSending}
                            onChange={(event) => {
                                setEditedTranslation(event.target.value);
                            }}
                            rows={4}
                            value={editedTranslation}
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2 sm:justify-between">
                    <Button
                        disabled={isSending}
                        onClick={() => {
                            onCancel();
                        }}
                        type="button"
                        variant="ghost"
                    >
                        Cancel
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            disabled={isSending}
                            onClick={() => {
                                void onSendEnglish();
                            }}
                            type="button"
                            variant="outline"
                        >
                            Send in English
                        </Button>
                        <Button
                            disabled={isSending || !editedTranslation.trim()}
                            onClick={() => {
                                void onSendTranslated(editedTranslation);
                            }}
                            type="button"
                        >
                            {isSending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : null}
                            Send translated
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
